import Fuse, { type IFuseOptions } from 'fuse.js';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Body1,
    Heading2,
    HorizontalDivider,
    OutlinedCard,
    SearchInput,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { NoClaimsIllustration, NoResultsIllustration } from '@phx/myphx-lib';

import { ConditionalComponent } from '../../../components/utility/ConditionalComponent';
import type {
    ClaimGeneratedPrescriberOrderInfoFragment,
    DigitallyReceivedPrescriberOrderInfoFragment,
    PriorAuthorizationInfoFragment,
} from '../../../graphql/generated/graphql';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import { sortPrescriptionsByDate } from '../../../util/cabinet/prescriber-orders';
import { isNewOrder } from '../../../util/orders';

import { HistorySearchItem } from './HistorySearchItem';

export type Prescription =
    | ClaimGeneratedPrescriberOrderInfoFragment
    | DigitallyReceivedPrescriberOrderInfoFragment
    | PriorAuthorizationInfoFragment;

type HistorySearchProps = {
    prescriptions: Prescription[];
};

const fuseOptions: IFuseOptions<Prescription> = {
    keys: [
        { name: 'productDescription', weight: 2 }, // default weight is 1
        'patient.firstName',
        'patient.lastName',
        'prescribedProduct.formValue',
        'prescribedProduct.strengthUnit',
    ],
    threshold: 0.1,
    distance: 100,
};

export const HistorySearch = (props: HistorySearchProps) => {
    const { prescriptions } = props;
    const { t } = useTranslation();
    const [search, setSearch] = useState('');

    const filteredPrescriptions = prescriptions.filter((prescription) => {
        const { isNonDrxPriorAuthorization } =
            // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
            usePriorAuthorizationDetails(prescription);
        return (
            prescription.__typename === 'DigitallyReceivedPrescriberOrder' ||
            (isNonDrxPriorAuthorization && !isNewOrder(prescription))
        );
    });

    const filtered = useMemo(() => {
        if (!search) {
            return filteredPrescriptions.sort(sortPrescriptionsByDate);
        }

        const fuse = new Fuse<Prescription>(filteredPrescriptions, fuseOptions);

        return fuse
            .search(search)
            .map((f) => f.item)
            .sort(sortPrescriptionsByDate);
    }, [filteredPrescriptions, search]);

    return (
        <Stack gap="lg">
            <SearchInput
                label={t('cabinet.history.searchInput.label')}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                clear={() => setSearch('')}
            />

            <ConditionalComponent
                condition={filteredPrescriptions.length === 0}
            >
                <Stack align="center" justify="center">
                    <NoClaimsIllustration />
                    <Heading2>{t('cabinet.history.empty.heading')}</Heading2>
                    <Body1 ta="center">{t('cabinet.history.empty.body')}</Body1>
                </Stack>
            </ConditionalComponent>

            <ConditionalComponent condition={filtered.length > 0}>
                <OutlinedCard>
                    <Stack gap="sm">
                        {filtered.map((p, i) => (
                            <Fragment key={p.id}>
                                {i > 0 && <HorizontalDivider />}
                                <HistorySearchItem item={p} />
                            </Fragment>
                        ))}
                    </Stack>
                </OutlinedCard>
            </ConditionalComponent>

            <ConditionalComponent
                condition={
                    !!filteredPrescriptions.length && filtered.length === 0
                }
            >
                <Stack align="center" justify="center">
                    <NoResultsIllustration />
                    <Body1 ta="center">{t('cabinet.history.noMatch')}</Body1>
                </Stack>
            </ConditionalComponent>
        </Stack>
    );
};
